import { Button, ButtonGroup, LoadingSpinner, Label, Icon, Tooltip } from '@opengov/component-library/capital';
import React, { useState } from 'react';
import { GenerateSummaryModalMessages } from '../../../types';
import { Modal } from '../Modal';
import styles from './AiGenerateSummaryModal.scss';
import {
  AiEnabledMediaType,
  HtmlExtractionResult
} from '../../../stories/components/StoryEditor/StoryEditor/TinyMCEStoryEditor/components/Div/htmlParsing/htmlParsingUtils';
import { AiGeneratedSummaryLength } from '../../../api/ai-api';
import { EditorValueSource } from '../../../stories/components/StoryEditor/StoryEditor/TinyMCEStoryEditor/tinyMCEStoryEditorUtils';
import { removeHtmlTags } from '../../../stories/utils/editor';

type Props = {
  closeModal(): void;
  messages: GenerateSummaryModalMessages;
  onGenerateSummary(
    aiEnabledMediaType: AiEnabledMediaType,
    contentLength: string
  ): Promise<{ summary: string; extraction: HtmlExtractionResult }>;
  afterGenerateSummary(extraction: HtmlExtractionResult, summary: string): void;
  mediaType: AiEnabledMediaType;
  editorValueSource: EditorValueSource;
};

export function AiGenerateSummaryModal({
  closeModal,
  messages,
  onGenerateSummary,
  afterGenerateSummary,
  mediaType,
  editorValueSource
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<string>('');
  const [extraction, setExtraction] = useState<HtmlExtractionResult | null>(null);
  const [contentLength, setContentLength] = useState<AiGeneratedSummaryLength>(AiGeneratedSummaryLength.SHORT);

  const generateSummary = async () => {
    setIsLoading(true);
    setSummary('');
    const { summary, extraction } = await onGenerateSummary(mediaType, contentLength);
    setExtraction(extraction);
    setSummary(summary);
    setIsLoading(false);
  };

  const insertAfterGenerateSummary = () => {
    afterGenerateSummary(extraction, summary);
    closeModal();
  };

  const handleCancel = () => {
    if ('resetValueSourceToUser' in editorValueSource) {
      editorValueSource.resetValueSourceToUser();
    }
    closeModal();
  };

  return (
    <Modal title={messages.generateReportSummary} onClose={handleCancel} dataTest="ai-generate-summary-modal">
      <div className={styles.container}>
        <p className={styles.label}>{messages.setPreferencesToGenerate}</p>
        <Label>{messages.contentLength}</Label>
        <ButtonGroup variant="neutral" data-test="view-button-group">
          <Button
            selected={contentLength === AiGeneratedSummaryLength.SHORT}
            onClick={() => setContentLength(AiGeneratedSummaryLength.SHORT)}
            disabled={isLoading}
          >
            {messages.short}
          </Button>
          <Button
            selected={contentLength === AiGeneratedSummaryLength.MEDIUM}
            onClick={() => setContentLength(AiGeneratedSummaryLength.MEDIUM)}
            disabled={isLoading}
          >
            {messages.medium}
          </Button>
          <Button
            selected={contentLength === AiGeneratedSummaryLength.LONG}
            onClick={() => setContentLength(AiGeneratedSummaryLength.LONG)}
            disabled={isLoading}
          >
            {messages.long}
          </Button>
        </ButtonGroup>
        {isLoading ? (
          <LoadingSpinner
            className={styles.paddingTop}
            dataTest="generate-summary-loader"
            message={messages.generatingSummary}
          />
        ) : (
          <div className={styles.paddingTop}>
            {summary && (
              <>
                <textarea
                  className={styles.textArea}
                  disabled={true}
                  placeholder="..."
                  value={removeHtmlTags(summary)}
                />
                <Tooltip className={styles.tooltip} content={messages.info} placement="top-end">
                  <div className={styles.learn}>
                    <Icon className={styles.icon} icon="utility-info" />
                    <div>Learn how this works</div>
                  </div>
                </Tooltip>
              </>
            )}
          </div>
        )}
        <div
          className={
            (!isLoading && summary.length === 0) || isLoading
              ? styles.buttonsContainerDisabled
              : styles.buttonsContainer
          }
        >
          {summary.length > 0 && (
            <Button variant="naked" onClick={() => generateSummary()} dataTest="regenerate-summary">
              <div className={styles.regenerate}>
                <Icon icon="action-refresh" className={styles.regenerateIcon}/>
                <div>Regenerate</div>
              </div>
            </Button>
          )}
          <div className={styles.buttons}>
            <Button variant="naked" onClick={() => handleCancel()} dataTest="cancel">
              {messages.cancel}
            </Button>
            {isLoading ? (
              <Button variant="primary" disabled data-test="generate-summary">
                {messages.generate}
              </Button>
            ) : summary ? (
              <Button variant="primary" onClick={() => insertAfterGenerateSummary()} dataTest="insert-summary">
                {messages.insert}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => generateSummary()} dataTest="generate-summary">
                {messages.generate}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
